import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Code, Copy, Check, ExternalLink, Globe, Smartphone, Monitor, Zap, Layout, FileCode, Link2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../../App';

const EmbedCodeGenerator = () => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedLang, setSelectedLang] = useState('en');
  const [clinic, setClinic] = useState(null);

  useEffect(() => {
    loadClinicAndConfig();
  }, []);

  const loadClinicAndConfig = async () => {
    try {
      const clinicRes = await axios.get(`${API}/clinic/branding`);
      setClinic(clinicRes.data);
      
      if (clinicRes.data?.slug) {
        // Show warning if using auto-generated slug
        if (clinicRes.data.has_configured_slug === false) {
          toast.warning('Your clinic URL slug is auto-generated. Configure a custom slug in Clinic Settings for a cleaner URL.');
        }
        
        const configRes = await axios.get(`${API}/embed/config/${clinicRes.data.slug}`);
        setEmbedConfig(configRes.data);
        
        // Show warning if clinic is not active
        if (configRes.data?.warning) {
          toast.warning(configRes.data.warning);
        }
      } else {
        console.error('Clinic slug not found in branding response');
        toast.error('Clinic slug not configured. Please set a URL slug in Clinic Settings.');
      }
    } catch (error) {
      console.error('Failed to load embed config:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load embed configuration';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(type);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const CodeBlock = ({ code, type, title }) => (
    <div className="relative">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyToClipboard(code, type)}
          className="h-8"
        >
          {copiedCode === type ? (
            <><Check className="h-4 w-4 mr-1 text-green-500" /> Copied!</>
          ) : (
            <><Copy className="h-4 w-4 mr-1" /> Copy</>
          )}
        </Button>
      </div>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!embedConfig) {
    return (
      <Card className="dark:bg-slate-800">
        <CardContent className="py-12 text-center">
          <Code className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500 font-medium">Unable to load embed configuration.</p>
          <p className="text-sm text-slate-400 mt-2">
            This could be because:
          </p>
          <ul className="text-sm text-slate-400 mt-2 space-y-1">
            <li>• Your clinic doesn't have a URL slug configured</li>
            <li>• Your clinic status is not "active"</li>
            <li>• There was a network error</li>
          </ul>
          <p className="text-sm text-slate-500 mt-4">
            Please check your clinic settings or contact support.
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { embed_codes, preview_url } = embedConfig;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Embed Widget</h2>
          <p className="text-slate-500 dark:text-slate-400">
            Add membership plans to any website - WordPress alternative
          </p>
        </div>
        <Button
          onClick={() => window.open(preview_url, '_blank')}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Preview Widget
        </Button>
      </div>

      {/* Features */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="dark:bg-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900 rounded-lg">
              <Monitor className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <p className="font-medium dark:text-white text-sm">Responsive</p>
              <p className="text-xs text-slate-500">All devices</p>
            </div>
          </CardContent>
        </Card>
        <Card className="dark:bg-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Globe className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium dark:text-white text-sm">Multi-language</p>
              <p className="text-xs text-slate-500">EN / ES</p>
            </div>
          </CardContent>
        </Card>
        <Card className="dark:bg-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Layout className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="font-medium dark:text-white text-sm">Landing Pages</p>
              <p className="text-xs text-slate-500">Single plan mode</p>
            </div>
          </CardContent>
        </Card>
        <Card className="dark:bg-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium dark:text-white text-sm">Fast Loading</p>
              <p className="text-xs text-slate-500">Async script</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Embed Options */}
      <Tabs defaultValue="all-plans" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all-plans">All Plans</TabsTrigger>
          <TabsTrigger value="single-plan">
            <Layout className="h-4 w-4 mr-1" />
            Landing Page
          </TabsTrigger>
          <TabsTrigger value="iframe">iFrame</TabsTrigger>
          <TabsTrigger value="button">Button</TabsTrigger>
          <TabsTrigger value="api">API</TabsTrigger>
        </TabsList>

        {/* All Plans Widget */}
        <TabsContent value="all-plans">
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {embed_codes.javascript_widget.name}
                    <Badge className="bg-teal-100 text-teal-700">Recommended</Badge>
                  </CardTitle>
                  <CardDescription>{embed_codes.javascript_widget.description}</CardDescription>
                </div>
                <Select value={selectedLang} onValueChange={setSelectedLang}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={selectedLang === 'es' ? embed_codes.javascript_widget.code_es : embed_codes.javascript_widget.code}
                type="widget"
                title="Copy and paste this code into your website"
              />
              
              <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">How to use:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <li>Copy the code above</li>
                  <li>Paste it where you want the widget to appear</li>
                  <li>The widget will display all your active membership plans</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Single Plan - Landing Pages */}
        <TabsContent value="single-plan">
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" />
                {embed_codes.single_plan?.name || 'Single Plan Widget'}
                <Badge className="bg-purple-100 text-purple-700">Landing Pages</Badge>
              </CardTitle>
              <CardDescription>
                {embed_codes.single_plan?.description || 'Perfect for dedicated landing pages for each plan'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center justify-end">
                <Select value={selectedLang} onValueChange={setSelectedLang}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {embed_codes.single_plan?.plans?.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {embed_codes.single_plan.plans.map((plan, index) => (
                    <AccordionItem key={plan.plan_id} value={`plan-${index}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <FileCode className="h-5 w-5 text-purple-600" />
                          <span className="font-medium">{plan.plan_name}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-2">
                          <CodeBlock
                            code={selectedLang === 'es' ? plan.code_es : plan.code_en}
                            type={`plan-${plan.plan_id}`}
                            title={`Widget code for "${plan.plan_name}"`}
                          />
                          
                          <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                            <Link2 className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-600 dark:text-slate-400">Direct link:</span>
                            <code className="text-sm bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded flex-1 overflow-x-auto">
                              {plan.direct_link}
                            </code>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(plan.direct_link, `link-${plan.plan_id}`)}
                            >
                              {copiedCode === `link-${plan.plan_id}` ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Layout className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No active plans found.</p>
                  <p className="text-sm">Create membership plans to generate embed codes.</p>
                </div>
              )}

              <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                  💡 Optimized for Landing Pages
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-purple-700 dark:text-purple-300">
                  <li>Centered layout with larger typography</li>
                  <li>Shows detailed benefits and features</li>
                  <li>Prominent call-to-action button</li>
                  <li>30-day satisfaction guarantee message</li>
                  <li>Perfect for Google Ads or social media campaigns</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iFrame */}
        <TabsContent value="iframe">
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle>{embed_codes.iframe.name}</CardTitle>
              <CardDescription>{embed_codes.iframe.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={embed_codes.iframe.code}
                type="iframe"
                title="iFrame embed code"
              />
              
              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Note:</h4>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  The iFrame method is simpler but has limited customization options. 
                  For best results, use the JavaScript Widget.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Button */}
        <TabsContent value="button">
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle>{embed_codes.button.name}</CardTitle>
              <CardDescription>{embed_codes.button.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={embed_codes.button.code}
                type="button"
                title="Button HTML code"
              />
              
              <div className="mt-6">
                <h4 className="font-medium text-slate-900 dark:text-white mb-3">Preview:</h4>
                <div 
                  className="p-6 bg-slate-100 dark:bg-slate-900 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: embed_codes.button.code }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API */}
        <TabsContent value="api">
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle>{embed_codes.api.name}</CardTitle>
              <CardDescription>{embed_codes.api.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">All Plans Endpoint</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="bg-green-100 text-green-700">GET</Badge>
                    <code className="flex-1 bg-slate-900 text-slate-100 p-3 rounded-lg text-sm overflow-x-auto">
                      {embed_codes.api.endpoint}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(embed_codes.api.endpoint, 'api')}
                    >
                      {copiedCode === 'api' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Single Plan Endpoint</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="bg-green-100 text-green-700">GET</Badge>
                    <code className="flex-1 bg-slate-900 text-slate-100 p-3 rounded-lg text-sm overflow-x-auto">
                      {embed_codes.api.single_plan_endpoint}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(embed_codes.api.single_plan_endpoint, 'api-single')}
                    >
                      {copiedCode === 'api-single' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Example Usage:</h4>
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
{`// Get all plans
fetch('${embed_codes.api.endpoint}')
  .then(res => res.json())
  .then(data => console.log(data.plans));

// Get single plan
fetch('${embed_codes.api.endpoint}?plan_id=YOUR_PLAN_ID')
  .then(res => res.json())
  .then(data => console.log(data.plans[0]));`}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmbedCodeGenerator;
